import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  LIBELLE_CATEGORIE_OPCVM,
  formaterDate,
  formaterMontant,
  formaterPourcentage,
  formaterQuantite,
  montant,
  poidsPourcent,
  somme,
  type CategorieOpcvm,
  type Poche,
} from '@gsm/partage';
import { messageErreur, useRessource } from '../api/ressource';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { JournalEspeces } from '../composants/JournalEspeces';
import { Nombre } from '../composants/Nombre';
import { Pastille } from '../composants/Pastille';
import { RegleAllocation, type LigneReglette } from '../composants/RegleAllocation';

interface PositionValo {
  instrumentId: string;
  quantite: string;
  cours: string;
  valeur: string;
  pru: string;
  plusValueLatente: string;
  poidsPct: string;
  poche: Poche;
  trancheMaturiteId: string | null;
  /** Aucun cours connu : la ligne est valorisée à son PRU. */
  coursAbsent: boolean;
  instrument: { libelle: string; type: string; opcvm: { categorie: string } | null };
}

interface Valorisation {
  dateValo: string;
  /** Date de la situation d'où viennent les positions — voir le bandeau d'écart. */
  dateSituation: string | null;
  valeurActions: string;
  valeurTaux: string;
  valeurOpcvm: string;
  tresorerie: string;
  actifNet: string;
  plusValueLatente: string;
  positions: PositionValo[];
}

interface Mandat {
  reference: string;
  profilId: string;
  client: { nom: string; prenom: string };
  profil: { libelle: string };
}

interface LigneCible {
  poche: Poche;
  pourcentage: string;
  instrumentId: string | null;
  trancheMaturiteId: string | null;
  categorieOpcvm: string | null;
  instrument: { libelle: string } | null;
  tranche: { libelle: string } | null;
}

const LIBELLE_POCHE: Record<Poche, string> = {
  ACTIONS: 'Poche actions',
  TAUX: 'Poche taux · BDT',
  OPCVM: 'Poche OPCVM',
};

export function Portefeuille() {
  const { id } = useParams<{ id: string }>();
  const mandat = useRessource<Mandat>(`/mandats/${id}`);
  const valo = useRessource<Valorisation>(`/valorisations/mandats/${id}`);
  const allocation = useRessource<{ lignes: LigneCible[] } | null>(
    mandat.donnees ? `/allocations/en-vigueur/${mandat.donnees.profilId}` : null,
  );

  const v = valo.donnees;
  const m = mandat.donnees;

  /**
   * La réglette prend enfin tout son sens : le poids RÉEL de chaque ligne
   * s'affiche en regard de sa cible. C'est l'écran que le gérant regarde le
   * plus (charte, § 6.4).
   */
  const reglettes = useMemo<Record<Poche, LigneReglette[]>>(() => {
    const vide: Record<Poche, LigneReglette[]> = { ACTIONS: [], TAUX: [], OPCVM: [] };
    if (!v || !allocation.donnees) return vide;

    const actifNet = montant(v.actifNet);
    for (const ligne of allocation.donnees.lignes) {
      const libelle =
        ligne.instrument?.libelle ??
        ligne.tranche?.libelle ??
        (ligne.categorieOpcvm
          ? LIBELLE_CATEGORIE_OPCVM[ligne.categorieOpcvm as CategorieOpcvm]
          : '—');

      // Valeur détenue rattachée à cette ligne d'allocation : la ligne action,
      // la tranche de maturité ou la catégorie d'OPCVM, jamais la poche entière.
      const detenu = somme(
        v.positions
          .filter((p) => {
            if (p.poche !== ligne.poche) return false;
            if (ligne.poche === 'ACTIONS') return p.instrumentId === ligne.instrumentId;
            if (ligne.poche === 'TAUX') return p.trancheMaturiteId === ligne.trancheMaturiteId;
            return p.instrument.opcvm?.categorie === ligne.categorieOpcvm;
          })
          .map((p) => montant(p.valeur)),
      );

      vide[ligne.poche].push({
        cle: `${ligne.poche}-${libelle}`,
        libelle,
        reelPct: poidsPourcent(detenu, actifNet).toFixed(4),
        ciblePct: ligne.pourcentage,
      });
    }
    return vide;
  }, [v, allocation.donnees]);

  if (valo.chargement || mandat.chargement) return <p className="surtitre">Chargement…</p>;

  /**
   * Le journal s'affiche même sans valorisation : la trésorerie ne dépend
   * d'aucun cours, et c'est précisément sur un mandat fraîchement alimenté
   * qu'on a besoin de la lire. Ici en aperçu — la chaîne entière et les
   * engagements à venir sont sur la fiche trésorerie.
   */
  const journal = (
    <JournalEspeces mandatId={id!} taille={10} apercu lienFiche={`/mandats/${id}/tresorerie`} />
  );

  if (valo.erreur || !v || !m)
    return (
      <>
        <p className="entete__fil">Valorisation</p>
        <BandeauAlerte nature="gestion" titre="Mandat non valorisé">
          {messageErreur(valo.erreur) ??
            "Ce mandat n'a pas encore été valorisé. Il y faut une situation reçue du back-office — sans elle on ignore ce qu'il détient — puis les cours du jour, puis le calcul."}
        </BandeauAlerte>
        {journal}
      </>
    );

  const colonnes: Colonne<PositionValo>[] = [
    { cle: 'libelle', entete: 'Instrument', rendu: (p) => p.instrument.libelle },
    { cle: 'poche', entete: 'Poche', rendu: (p) => p.poche },
    { cle: 'quantite', entete: 'Quantité', num: true, rendu: (p) => formaterQuantite(p.quantite) },
    { cle: 'pru', entete: 'PRU', num: true, rendu: (p) => formaterMontant(p.pru) },
    {
      cle: 'cours',
      entete: 'Cours',
      num: true,
      // Le PRU retenu faute de cours n'est pas un cours : on ne l'affiche pas comme tel.
      rendu: (p) => formaterMontant(p.coursAbsent ? null : p.cours),
    },
    { cle: 'valeur', entete: 'Valeur', num: true, rendu: (p) => <Nombre valeur={p.valeur} /> },
    { cle: 'poids', entete: 'Poids', num: true, rendu: (p) => formaterPourcentage(p.poidsPct) },
    {
      cle: 'pv',
      entete: '+/− latente',
      num: true,
      rendu: (p) => <Nombre valeur={p.coursAbsent ? null : p.plusValueLatente} colorer />,
    },
  ];

  const sansCours = v.positions.filter((p) => p.coursAbsent);

  return (
    <>
      <p className="entete__fil">
        <Link to="/mandats">Mandats</Link> › <Link to={`/mandats/${id}`}>{m.reference}</Link> ›
        Portefeuille
      </p>
      <div className="entete">
        <h1>
          {m.client.prenom} {m.client.nom}
        </h1>
        <span className="entete__ref">{m.reference}</span>
        <Pastille ton="neutre">{m.profil.libelle}</Pastille>
      </div>

      {v.dateSituation && v.dateSituation.slice(0, 10) !== v.dateValo.slice(0, 10) && (
        <BandeauAlerte nature="gestion" titre="Deux dates dans le même chiffre">
          Les positions sont celles de la situation du {formaterDate(v.dateSituation)}, valorisées
          aux cours du {formaterDate(v.dateValo)}. Le back-office n’a rien envoyé entre les deux :
          tout mouvement survenu depuis manque à l’appel.
        </BandeauAlerte>
      )}

      {sansCours.length > 0 && (
        <BandeauAlerte nature="gestion" titre="Lignes sans cours">
          {sansCours.map((p) => p.instrument.libelle).join(', ')}{' '}
          {sansCours.length > 1 ? 'n’ont' : 'n’a'} aucun cours au {formaterDate(v.dateValo)} :{' '}
          {sansCours.length > 1
            ? 'elles sont valorisées à leur prix de revient'
            : 'elle est valorisée à son prix de revient'}
          , sans plus-value latente. Importer le cours puis relancer la valorisation.
        </BandeauAlerte>
      )}

      <section className="cles" aria-label="Chiffres clés">
        <div className="cle">
          <span className="surtitre">Actif net</span>
          <div className="cle__valeur">
            {formaterMontant(v.actifNet)}
            <small>MAD</small>
          </div>
          <div className="appoint">cours du {formaterDate(v.dateValo)}</div>
        </div>
        <div className="cle">
          <span className="surtitre">Plus-value latente</span>
          <div className="cle__valeur">
            <Nombre valeur={v.plusValueLatente} colorer />
          </div>
        </div>
      </section>

      {(['ACTIONS', 'TAUX', 'OPCVM'] as Poche[]).map((poche) =>
        reglettes[poche].length > 0 ? (
          <Bloc key={poche} titre={LIBELLE_POCHE[poche]} meta="réel contre cible">
            <RegleAllocation lignes={reglettes[poche]} />
          </Bloc>
        ) : null,
      )}

      <Bloc titre="Positions" meta={`${v.positions.length} ligne(s)`}>
        <Tableau
          colonnes={colonnes}
          lignes={v.positions}
          cle={(p) => p.instrumentId}
          vide="Aucune position."
        />
        <div className="totaux totaux--droite">
          <div>
            <span>Actions</span>
            <Nombre valeur={v.valeurActions} />
          </div>
          <div>
            <span>Taux</span>
            <Nombre valeur={v.valeurTaux} />
          </div>
          <div>
            <span>OPCVM</span>
            <Nombre valeur={v.valeurOpcvm} />
          </div>
          <div>
            <span>Trésorerie</span>
            <Nombre valeur={v.tresorerie} />
          </div>
          <div>
            <span>
              <b>Actif net</b>
            </span>
            <Nombre valeur={v.actifNet} />
          </div>
        </div>
      </Bloc>

      {journal}
    </>
  );
}
